import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquare, Send, ArrowLeft, Search, Circle } from "lucide-react";

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const MessagesPage = () => {
  const { user, api } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [thread, setThread] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);
  const pollRef = useRef(null);

  const loadContacts = useCallback(() => {
    api().get("/messages/contacts").then(r => setContacts(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const loadThread = useCallback((contactId) => {
    api().get(`/messages/thread/${contactId}`).then(r => {
      setThread(r.data);
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!selectedContact) return;
    loadThread(selectedContact.id);
    // Poll every 5s
    pollRef.current = setInterval(() => {
      loadThread(selectedContact.id);
      loadContacts();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedContact, loadThread, loadContacts]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedContact) return;
    setSending(true);
    try {
      await api().post("/messages", { to_user_id: selectedContact.id, content: newMsg.trim() });
      setNewMsg("");
      loadThread(selectedContact.id);
      loadContacts();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const filteredContacts = contacts.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
  const totalUnread = contacts.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div data-testid="messages-page" style={{ display: 'flex', height: 'calc(100vh - 0px)', overflow: 'hidden' }}>
      {/* ─── Contact List ─── */}
      <div data-testid="contact-list" style={{
        width: selectedContact ? 320 : '100%', maxWidth: 380, borderRight: '1px solid var(--dark-border)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: 'width 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid var(--dark-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={22} style={{ color: 'var(--cyan)' }} /> Messages
            </h1>
            {totalUnread > 0 && (
              <span data-testid="total-unread" style={{ background: '#ff6b00', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>{totalUnread}</span>
            )}
          </div>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input data-testid="contact-search" className="input-field" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 36, fontSize: 13 }} />
          </div>
        </div>

        {/* Contact Items */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredContacts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              <MessageSquare size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ fontSize: 13 }}>No contacts available</p>
              <p style={{ fontSize: 11, marginTop: 4 }}>
                {user?.role === "player_parent" ? "Your teammates will appear here" : "Members in your scope will appear here"}
              </p>
            </div>
          ) : filteredContacts.map(c => (
            <div key={c.id} data-testid={`contact-${c.id}`}
              onClick={() => { setSelectedContact(c); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedContact(c); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                cursor: 'pointer', borderBottom: '1px solid var(--dark-border)',
                background: selectedContact?.id === c.id ? 'rgba(0,229,255,0.08)' : 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (selectedContact?.id !== c.id) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
              onMouseLeave={e => { if (selectedContact?.id !== c.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: c.role === 'coach' ? '#ff6b00' : c.role === 'org_admin' ? '#9c27b0' : '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  {c.name?.charAt(0)}
                </div>
                {c.unread_count > 0 && (
                  <div style={{ position: 'absolute', top: -2, right: -2, width: 18, height: 18, borderRadius: '50%', background: '#ff6b00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', border: '2px solid var(--dark-bg)' }}>{c.unread_count}</div>
                )}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ fontSize: 14, fontWeight: c.unread_count > 0 ? 700 : 500, color: 'var(--text-primary)' }}>{c.name}</p>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>{formatTime(c.last_message_time)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ fontSize: 12, color: c.unread_count > 0 ? 'var(--text-primary)' : 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{c.last_message || <span style={{ fontStyle: 'italic' }}>No messages yet</span>}</p>
                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', flexShrink: 0 }}>{c.role?.replace('_', ' ')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Chat Thread ─── */}
      {selectedContact ? (
        <div data-testid="chat-thread" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Chat Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--dark-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button data-testid="chat-back-btn" onClick={() => setSelectedContact(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><ArrowLeft size={20} /></button>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: selectedContact.role === 'coach' ? '#ff6b00' : selectedContact.role === 'org_admin' ? '#9c27b0' : '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>{selectedContact.name?.charAt(0)}</div>
            <div>
              <p data-testid="chat-contact-name" style={{ fontSize: 15, fontWeight: 600 }}>{selectedContact.name}</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{selectedContact.role?.replace('_', ' ')} &bull; {selectedContact.email}</p>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px' }}>
            {thread.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                <p style={{ fontSize: 14 }}>No messages yet</p>
                <p style={{ fontSize: 12, marginTop: 4 }}>Send a message to start the conversation</p>
              </div>
            )}
            {thread.map(msg => {
              const isMe = msg.from_user_id === user?.id;
              return (
                <div key={msg.id} data-testid={`msg-${msg.id}`} style={{
                  display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 10,
                }}>
                  <div style={{
                    maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
                    background: isMe ? '#ff6b00' : 'var(--dark-card)',
                    border: isMe ? 'none' : '1px solid var(--dark-border)',
                    borderBottomRightRadius: isMe ? 4 : 12,
                    borderBottomLeftRadius: isMe ? 12 : 4,
                  }}>
                    <p style={{ fontSize: 14, color: isMe ? '#fff' : 'var(--text-primary)', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.content}</p>
                    <p style={{ fontSize: 10, color: isMe ? 'rgba(255,255,255,0.6)' : 'var(--text-secondary)', marginTop: 4, textAlign: isMe ? 'right' : 'left' }}>{formatTime(msg.created_at)}</p>
                  </div>
                </div>
              );
            })}
            <div ref={threadEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--dark-border)', display: 'flex', gap: 10 }}>
            <input data-testid="message-input" className="input-field" placeholder="Type a message..." value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={handleKeyDown} style={{ flex: 1, fontSize: 14 }} />
            <button data-testid="send-message-btn" onClick={sendMessage} disabled={sending || !newMsg.trim()} style={{
              width: 44, height: 44, borderRadius: 10, background: newMsg.trim() ? '#ff6b00' : 'var(--dark-card)',
              border: newMsg.trim() ? 'none' : '1px solid var(--dark-border)',
              color: newMsg.trim() ? '#fff' : 'var(--text-secondary)',
              cursor: newMsg.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}><Send size={18} /></button>
          </div>
        </div>
      ) : (
        /* Empty state when no contact selected */
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <MessageSquare size={56} style={{ color: 'var(--text-secondary)', opacity: 0.2, marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Select a conversation</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Choose a contact to start messaging</p>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
